import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Field } from '@mailflow/ui';
import { checkSpamScore } from '@mailflow/shared';
import { listsQuery } from '../lib/contacts.js';
import { segmentsQuery } from '../lib/segments.js';
import {
  useCreateCampaign,
  useUpdateCampaign,
  useEmpfaengerVorschau,
  scheduleCampaignById,
  startCampaignById,
  sendingPoliciesQuery,
  describeWindows,
  campaignQuery,
} from '../lib/campaigns.js';
import { ApiRequestError } from '../lib/api.js';
import { SlashCommandToolbar } from '../components/SlashCommandMenu.js';
import { AudienceBlock, type AudienceRef } from '../components/AudienceBlock.js';
import { TestSendBox } from '../components/TestSendBox.js';
import { StartDialog } from '../components/StartDialog.js';

interface VariantDraft {
  key: string;
  subject: string;
  html: string;
}

const TEMPLATES: Array<{ name: string; subject: string; html: string }> = [
  {
    name: 'Willkommen & Erstkontakt',
    subject: 'Guten Tag {{vorname|zusammen}} — Kurze Frage zu {{firma|Ihrem Unternehmen}}',
    html: `<p>{Guten Tag|Hallo|Grüezi} {{vorname|zusammen}},</p>
<p>vielen Dank für Ihr Interesse an unseren Lösungen bei {{firma|Ihrer Firma}}.</p>
<p>Gerne würde ich Ihnen in einem kurzen 5-Minuten-Gespräch zeigen, wie wir Unternehmen in Ihrer Branche unterstützen.</p>
<p><a href="https://example.com/termin">Termin vereinbaren</a></p>
<p>{Beste Grüße|Freundliche Grüße|Herzliche Grüße},<br/>Max Mustermann</p>`,
  },
  {
    name: 'Angebot & Follow-up',
    subject: 'Follow-Up: Prüfung von Google-Bewertungen für {{firma|Ihr Unternehmen}}',
    html: `<p>{Grüezi|Guten Tag} {{vorname|zusammen}},</p>
<p>ich nehme Bezug auf unser letztes Gespräch bezüglich der Prüfung potenziell policywidriger Bewertungen für {{firma|Ihr Unternehmen}}.</p>
<p>Haben Sie diese Woche Zeit für eine kurze Rücksprache?</p>
<p><a href="https://example.com/details">Details ansehen</a></p>
<p>{Freundliche Grüsse|Beste Grüsse},<br/>Ihr Team</p>`,
  },
  {
    name: 'Schlanke Text-Mail',
    subject: 'Kurze Nachfrage',
    html: `<p>{Hallo|Guten Tag} {{vorname|zusammen}},</p>
<p>haben Sie meine letzte Nachricht erhalten?</p>
<p>Über eine kurze Rückmeldung würde ich mich freuen.</p>`,
  },
];

const DEFAULT_TPL = TEMPLATES[0] ?? {
  name: 'Standard',
  subject: 'Betreff',
  html: '<p>Hallo {{vorname|zusammen}},</p>',
};

/** Ersetzt Variablen wie {{vorname|zusammen}} und Spintax für die Live-Vorschau. */
function renderLivePreview(
  html: string,
  subject: string,
  includeUnsubscribeFooter = false,
  seed = 0,
): { previewSubject: string; previewHtml: string } {
  const sampleData: Record<string, string> = {
    vorname: 'Max',
    firstname: 'Max',
    nachname: 'Mustermann',
    lastname: 'Mustermann',
    name: 'Max Mustermann',
    fullname: 'Max Mustermann',
    firma: 'Mustermann AG',
    company: 'Mustermann AG',
    email: 'max.mustermann@example.com',
    ort: 'Zürich',
    city: 'Zürich',
    anrede: 'Herr',
  };

  const processSpintax = (text: string) => {
    let result = text;
    let iterations = 0;
    const SPINTAX_REGEX = /\{([^{}]+?\|[^{}]+?)\}/g;
    while (SPINTAX_REGEX.test(result) && iterations < 10) {
      result = result.replace(SPINTAX_REGEX, (_, choices: string) => {
        const options = choices.split('|');
        if (options.length === 0) return '';
        if (seed > 0) {
          const idx = (seed + iterations) % options.length;
          return options[idx]?.trim() ?? '';
        }
        return options[0]?.trim() ?? '';
      });
      iterations += 1;
    }
    return result;
  };

  const processMerge = (text: string) => {
    return text.replace(/\{\{\s*([a-z0-9_]+)(?:\|([^}]+))?\s*\}\}/gi, (_, key, fallback) => {
      const k = key.toLowerCase();
      return sampleData[k] ?? (fallback !== undefined ? fallback : key);
    });
  };

  const combinedSubject = processMerge(processSpintax(subject || 'Kein Betreff'));
  let combinedHtml = processMerge(processSpintax(html || '<p class="muted">Kein Inhalt eingegeben.</p>'));

  if (includeUnsubscribeFooter) {
    combinedHtml += `
<div style="margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center;">
  Sie erhalten diese E-Mail als geschäftlicher Ansprechpartner. Wenn Sie keine weiteren Nachrichten wünschen, können Sie sich <a href="#" style="color: #64748b; text-decoration: underline;">hier abmelden</a>.
</div>`;
  }

  return {
    previewSubject: combinedSubject,
    previewHtml: combinedHtml,
  };
}

export function CampaignEditorPage() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const isValidUuid = Boolean(
    id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id),
  );
  const initialId = isValidUuid ? (id as string) : null;
  const navigate = useNavigate();
  const lists = useQuery(listsQuery);
  const segments = useQuery(segmentsQuery);
  const sendingPolicies = useQuery(sendingPoliciesQuery);
  const create = useCreateCampaign();

  const existingCampaign = useQuery({
    ...campaignQuery(initialId ?? ''),
    enabled: Boolean(initialId),
  });

  const [name, setName] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [type, setType] = useState<'regular' | 'direct'>('direct');

  /*
   * Zielgruppe: mehrere Listen und Segmente, einschliessend wie
   * ausschliessend. Vorher war beides genau eine Zeichenkette — wer zwei
   * Listen anschreiben wollte, musste zwei Kampagnen anlegen, obwohl das
   * Datenmodell Arrays schon immer konnte.
   */
  const [include, setInclude] = useState<AudienceRef[]>([]);
  const [exclude, setExclude] = useState<AudienceRef[]>([]);
  const [includeUnsubscribeFooter, setIncludeUnsubscribeFooter] = useState<boolean>(false);
  const [enforceManual, setEnforceManual] = useState<boolean>(true);
  const [enforceUnsubscribe, setEnforceUnsubscribe] = useState<boolean>(true);
  const [enforceNoInterest, setEnforceNoInterest] = useState<boolean>(true);
  const [excludeHasWonDeal, setExcludeHasWonDeal] = useState<boolean>(true);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const segId = searchParams.get('segmentId');
    if (segId) {
      setInclude([{ kind: 'segment', id: segId }]);
    }
  }, []);

  /*
   * Sobald der Entwurf einmal gesichert ist, steht hier seine Kennung — und
   * jedes weitere Sichern aendert ihn, statt eine zweite Kampagne anzulegen.
   * Ohne das entstuende bei jedem Testversand eine neue.
   */
  const [campaignId, setCampaignId] = useState<string | null>(initialId);
  const [startdialog, setStartdialog] = useState(false);
  // Die Kennung steht erst nach dem ersten Sichern fest; bis dahin laeuft der
  // Hook auf einer leeren Kennung und wird nicht aufgerufen.
  const update = useUpdateCampaign(campaignId ?? '');

  // Sendefenster / Limits & Terminierung
  const [sendingPolicyId, setSendingPolicyId] = useState<string>('');
  const [scheduleMode, setScheduleMode] = useState<'now' | 'scheduled'>('now');
  const [scheduledDateTime, setScheduledDateTime] = useState('');

  const [viewMode, setViewMode] = useState<'split' | 'editor' | 'preview'>('split');
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);

  const [variants, setVariants] = useState<VariantDraft[]>([
    { key: 'A', subject: DEFAULT_TPL.subject, html: DEFAULT_TPL.html },
  ]);

  /*
   * Bestehende Kampagnendaten beim Bearbeiten einmalig vorausfüllen.
   *
   * Der Merker ist nötig, nicht nur schön: react-query lädt beim Zurückkehren
   * ins Fenster neu, und ohne ihn hätte jeder solche Nachlauf die Eingaben
   * überschrieben — mitten im Schreiben wäre der Betreff wieder der
   * gespeicherte gewesen.
   *
   * setState im Effekt ist hier der Sinn der Sache: Serverdaten werden einmal
   * in einen bearbeitbaren Entwurf überführt. Die Regel kennt diesen Fall
   * nicht und wird deshalb an genau dieser Stelle abgeschaltet.
   */
  const vorbefuelltFuer = useRef<string | null>(null);
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (existingCampaign.data) {
      const { campaign, variants: existingVariants } = existingCampaign.data;
      if (vorbefuelltFuer.current === campaign.id) return;
      vorbefuelltFuer.current = campaign.id;
      setCampaignId(campaign.id);
      setName(campaign.name);
      setFromName(campaign.fromName);
      setFromEmail(campaign.fromEmail);
      setType(campaign.type === 'direct' ? 'direct' : 'regular');
      setSendingPolicyId(campaign.sendingPolicyId ?? '');

      const aud = campaign.audience as {
        include?: AudienceRef[];
        exclude?: AudienceRef[];
        includeUnsubscribeFooter?: boolean;
      } | undefined;
      if (aud?.include) setInclude(aud.include);
      if (aud?.exclude) setExclude(aud.exclude);
      if (aud?.includeUnsubscribeFooter !== undefined) {
        setIncludeUnsubscribeFooter(aud.includeUnsubscribeFooter);
      } else {
        setIncludeUnsubscribeFooter(campaign.type !== 'direct');
      }

      if (existingVariants && existingVariants.length > 0) {
        setVariants(existingVariants.map((v) => ({ key: v.key, subject: v.subject, html: v.html })));
      }

      if (campaign.scheduledAt) {
        setScheduleMode('scheduled');
        setScheduledDateTime(new Date(campaign.scheduledAt).toISOString().slice(0, 16));
      }
    }
  }, [existingCampaign.data]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const activeVariant: VariantDraft = variants[activeVariantIndex] ?? variants[0] ?? {
    key: 'A',
    subject: '',
    html: DEFAULT_TPL.html,
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        void sichereEntwurf();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        setStartdialog(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [name, fromName, fromEmail, include, exclude, variants, sendingPolicyId, scheduleMode, scheduledDateTime]);

  const fieldErrors = create.error instanceof ApiRequestError ? (create.error.fields ?? {}) : {};
  const generalError =
    create.error instanceof ApiRequestError && Object.keys(fieldErrors).length === 0
      ? create.error.message
      : null;

  function updateVariant(index: number, patch: Partial<VariantDraft>) {
    setVariants((current) => current.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  }

  function applyTemplate(tpl: (typeof TEMPLATES)[0]) {
    if (!tpl) return;
    updateVariant(activeVariantIndex, { subject: tpl.subject, html: tpl.html });
  }

  /** Der Entwurf in der Form, die API und Testversand erwarten. */
  function entwurf() {
    return {
      name,
      fromName,
      fromEmail,
      type,
      sendingPolicyId: sendingPolicyId || null,
      audience: {
        include,
        exclude,
        includeUnsubscribeFooter,
        enforceManual,
        enforceUnsubscribe,
        enforceNoInterest,
        excludeHasWonDeal,
      },
      variants: variants.map((v) => ({ ...v, weight: 1 })),
    };
  }

  /**
   * Entwurf sichern — anlegen beim ersten Mal, danach aktualisieren.
   *
   * Der Testversand braucht eine gespeicherte Kampagne. Ohne diesen Weg
   * muesste der Nutzer erst speichern, die Seite verlassen, zurueckkommen und
   * dann testen — also genau die Umstaendlichkeit, die dieser Auftrag
   * abschaffen soll.
   */
  async function sichereEntwurf(): Promise<string> {
    if (campaignId) {
      await update.mutateAsync({ id: campaignId, ...entwurf() });
      return campaignId;
    }
    const result = await create.mutateAsync({ ...entwurf(), scheduledAt: null });
    setCampaignId(result.campaign.id);
    return result.campaign.id;
  }

  async function save() {
    try {
      const id = await sichereEntwurf();

      if (scheduleMode === 'scheduled' && scheduledDateTime) {
        await scheduleCampaignById(id, new Date(scheduledDateTime).toISOString());
      } else {
        await startCampaignById(id);
      }
      await navigate({ to: '/kampagnen/$id', params: { id } });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Versand konnte nicht gestartet werden.');
    }
  }

  const ready =
    name.trim() &&
    fromName.trim() &&
    fromEmail.trim() &&
    include.length > 0 &&
    (scheduleMode === 'now' || (scheduleMode === 'scheduled' && Boolean(scheduledDateTime))) &&
    variants.every((v) => v.subject.trim());

  const [spintaxSeed, setSpintaxSeed] = useState(0);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  const preview = renderLivePreview(
    activeVariant.html,
    activeVariant.subject,
    includeUnsubscribeFooter,
    spintaxSeed,
  );

  const writePreviewDoc = useCallback(() => {
    const iframe = previewIframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: #1f2937;
      background-color: #ffffff;
      padding: 16px;
      margin: 0;
      word-break: break-word;
    }
    a { color: #2563eb; }
    p { margin: 0 0 1em 0; }
    p:last-child { margin-bottom: 0; }
    table { max-width: 100%; border-collapse: collapse; }
    img { max-width: 100%; height: auto; }
  </style>
</head>
<body>${preview.previewHtml}</body>
</html>`);
        doc.close();
      }
    } catch {
      // Ignoriere Cross-Origin Einschränkungen
    }
  }, [preview.previewHtml]);

  useEffect(() => {
    writePreviewDoc();
  }, [writePreviewDoc, device]);
  const spam = checkSpamScore(activeVariant.subject, activeVariant.html);
  const selectedPolicy = sendingPolicies.data?.items.find((p) => p.id === sendingPolicyId);
  // Dieselbe Abfrage wie im Empfängerblock — geteilt über den Abfrageschlüssel,
  // damit Dialog und Block nicht zwei verschiedene Zahlen zeigen können.
  const { netto } = useEmpfaengerVorschau({ include, exclude });

  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <nav className="breadcrumb text-sm">
        <Link to="/kampagnen">Kampagnen</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">{campaignId ? 'Bearbeiten' : 'Neue Kampagne'}</span>
      </nav>

      <header className="page-header">
        <div>
          <h1 className="text-2xl">{campaignId ? 'Kampagne bearbeiten' : 'Kampagne anlegen'}</h1>
          <p className="muted text-sm">
            E-Mail-Editor mit Live-Vorschau, A/B-Tests, Ausschluss-Listen &amp; Terminierung.
          </p>
        </div>
        <div className="cluster">
          <div className="button-group">
            <Button
              variant={viewMode === 'split' ? 'primary' : 'ghost'}
              onClick={() => setViewMode('split')}
            >
              Split View ◧
            </Button>
            <Button
              variant={viewMode === 'editor' ? 'primary' : 'ghost'}
              onClick={() => setViewMode('editor')}
            >
              Nur Editor ✎
            </Button>
            <Button
              variant={viewMode === 'preview' ? 'primary' : 'ghost'}
              onClick={() => setViewMode('preview')}
            >
              Vorschau 👁️
            </Button>
          </div>
        </div>
      </header>

      {generalError && (
        <div className="alert" data-tone="danger" role="alert">
          {generalError}
        </div>
      )}

      {/* GRUNDDATEN & SENDER */}
      <section className="card">
        <div className="card__body stack">
          <h2 className="text-lg">1. Grunddaten &amp; Absender</h2>
          <Field
            label="Name der Kampagne"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Q3 B2B Neukunden-Gewinnung"
            hint="Nur intern in der Übersicht sichtbar."
            {...(fieldErrors.name ? { error: fieldErrors.name } : {})}
          />
          <div className="grid-2">
            <Field
              label="Absendername"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="z. B. Max Mustermann"
              {...(fieldErrors.fromName ? { error: fieldErrors.fromName } : {})}
            />
            <Field
              label="Absenderadresse"
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="max@beispiel.ch"
              {...(fieldErrors.fromEmail ? { error: fieldErrors.fromEmail } : {})}
            />
          </div>

          <div className="field">
            <label className="field__label">Versandart &amp; Posteingangs-Optimierung</label>
            <div className="grid-2">
              <label
                className={`card card--clickable ${type === 'direct' ? 'card--active' : ''}`}
                style={{ padding: '0.75rem', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <input
                    type="radio"
                    name="campaignType"
                    value="direct"
                    checked={type === 'direct'}
                    onChange={() => setType('direct')}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <div>
                    <strong>Direkte Firmen-E-Mail ✉️ (Empfohlen)</strong>
                    <p className="text-xs text-muted" style={{ marginTop: '0.25rem' }}>
                      Ohne Newsletter-Bulk-Header. Landet bei Gmail &amp; Outlook direkt im <strong>Haupt-Posteingang (Primary Inbox)</strong>.
                    </p>
                  </div>
                </div>
              </label>
              <label
                className={`card card--clickable ${type === 'regular' ? 'card--active' : ''}`}
                style={{ padding: '0.75rem', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <input
                    type="radio"
                    name="campaignType"
                    value="regular"
                    checked={type === 'regular'}
                    onChange={() => setType('regular')}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <div>
                    <strong>Klassischer Newsletter 📢</strong>
                    <p className="text-xs text-muted" style={{ marginTop: '0.25rem' }}>
                      Mit One-Click-Abmeldeheader (RFC 8058). Wird bei Gmail oft im Ordner „Werbung“ einsortiert.
                    </p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/*
            Der Testversand steht hier oben und nicht am Ende: was man vor
            jedem Versand tun soll, darf nicht gesucht werden müssen.
          */}
          <TestSendBox
            campaignId={campaignId}
            onEntwurfSichern={sichereEntwurf}
            variantKey={activeVariant.key}
            bereit={Boolean(name.trim() && fromName.trim() && fromEmail.trim() && activeVariant.subject.trim())}
          />
        </div>
      </section>

      {/* ZIELGRUPPE — mehrere Listen und Segmente, mit Herleitung */}
      <section className="card">
        <div className="card__body stack">
          <h2 className="text-lg">2. Empf&auml;nger</h2>
          <AudienceBlock
            listen={(lists.data?.items ?? []).map((l) => ({
              id: l.id,
              name: l.name,
              anzahl: l.contactCount,
            }))}
            segmente={(segments.data?.items ?? []).map((s) => ({
              id: s.id,
              name: s.name,
              anzahl: s.memberCount,
            }))}
            include={include}
            exclude={exclude}
            enforceManual={enforceManual}
            enforceUnsubscribe={enforceUnsubscribe}
            enforceNoInterest={enforceNoInterest}
            excludeHasWonDeal={excludeHasWonDeal}
            onChange={(inc, exc, toggles) => {
              setInclude(inc);
              setExclude(exc);
              if (toggles) {
                setEnforceManual(toggles.enforceManual);
                setEnforceUnsubscribe(toggles.enforceUnsubscribe);
                setEnforceNoInterest(toggles.enforceNoInterest);
                setExcludeHasWonDeal(toggles.excludeHasWonDeal);
              }
            }}
          />
        </div>
      </section>


      {/* VERSANDSCHEDULING & PROFIL / LIMITS */}
      <section className="card">
        <div className="card__body stack">
          <h2 className="text-lg">3. Versandplanung &amp; Stundenlimits</h2>

          <div className="grid-2">
            <div className="field">
              <label className="field__label">Versandzeitpunkt (Schedule)</label>
              <div className="cluster" style={{ gap: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="scheduleMode"
                    value="now"
                    checked={scheduleMode === 'now'}
                    onChange={() => setScheduleMode('now')}
                  />
                  <span>Sofort versenden</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="scheduleMode"
                    value="scheduled"
                    checked={scheduleMode === 'scheduled'}
                    onChange={() => setScheduleMode('scheduled')}
                  />
                  <span>Terminieren (Datum &amp; Uhrzeit)</span>
                </label>
              </div>

              {scheduleMode === 'scheduled' && (
                <div style={{ marginTop: 'var(--space-3)' }}>
                  <input
                    type="datetime-local"
                    className="input"
                    value={scheduledDateTime}
                    onChange={(e) => setScheduledDateTime(e.target.value)}
                  />
                  <span className="field__hint">
                    Die Kampagne wartet bis zu diesem Zeitpunkt und startet dann automatisch.
                  </span>
                </div>
              )}
            </div>

            <div className="field">
              <label className="field__label" htmlFor="sending-policy-select">
                Sendeprofil &amp; Ratenlimit (Stundenbegrenzung)
              </label>
              <select
                id="sending-policy-select"
                className="input"
                value={sendingPolicyId}
                onChange={(e) => setSendingPolicyId(e.target.value)}
              >
                <option value="">Standard (Unbegrenzte Versandgeschwindigkeit)</option>
                {sendingPolicies.data?.items.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.emailsPerHour > 0 ? `(${p.emailsPerHour} Mails/Std)` : '(Kein Stundenlimit)'}
                  </option>
                ))}
              </select>
              {selectedPolicy && (
                <div className="field__hint" style={{ marginTop: 'var(--space-2)' }}>
                  ⚡ Max. {selectedPolicy.emailsPerHour || '∞'} Mails/Std
                  {selectedPolicy.emailsPerDay > 0 ? ` | ${selectedPolicy.emailsPerDay} Mails/Tag` : ''}
                  {' | ⏰ '}
                  {describeWindows(selectedPolicy.sendWindows)}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* EDITOR & PREVIEW SPLIT CONTAINER */}
      <div className="editor-split-container" data-view={viewMode}>
        {/* EDITOR COLUMN */}
        {(viewMode === 'split' || viewMode === 'editor') && (
          <section className="card">
            <div className="card__body stack">
              <div className="page-header" style={{ alignItems: 'baseline' }}>
                <h2 className="text-lg">
                  {variants.length > 1 ? 'Varianten (A/B-Test)' : '4. E-Mail Inhalts-Editor'}
                </h2>
                {variants.length < 4 && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const newIndex = variants.length;
                      setVariants((current) => [
                        ...current,
                        {
                          key: String.fromCharCode(65 + current.length),
                          subject: activeVariant.subject,
                          html: activeVariant.html,
                        },
                      ]);
                      setActiveVariantIndex(newIndex);
                    }}
                  >
                    + Variante hinzufügen
                  </Button>
                )}
              </div>

              {/* VARIANT TABS */}
              {variants.length > 1 && (
                <div role="tablist" className="tabs__list">
                  {variants.map((v, idx) => (
                    <button
                      key={v.key}
                      type="button"
                      className={`tabs__tab ${idx === activeVariantIndex ? 'is-active' : ''}`}
                      onClick={() => setActiveVariantIndex(idx)}
                    >
                      Variante {v.key}
                    </button>
                  ))}
                </div>
              )}

              {/* TEMPLATE PICKER */}
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="text-sm muted">Vorlage laden:</span>
                {TEMPLATES.map((tpl) => (
                  <Button key={tpl.name} variant="ghost" onClick={() => applyTemplate(tpl)}>
                    {tpl.name}
                  </Button>
                ))}
              </div>

              <div className="variant">
                <div className="variant__head">
                  <strong>Variante {activeVariant.key}</strong>
                  {variants.length > 1 && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setVariants((c) => c.filter((_, i) => i !== activeVariantIndex));
                        setActiveVariantIndex(0);
                      }}
                    >
                      Entfernen
                    </Button>
                  )}
                </div>

                <Field
                  label="Betreffzeile"
                  value={activeVariant.subject}
                  onChange={(e) => updateVariant(activeVariantIndex, { subject: e.target.value })}
                  hint="Variablen & Spintax möglich: z. B. {Guten Tag|Hallo} {{vorname|zusammen}}"
                />

                <div className="field">
                  <label className="field__label" htmlFor={`html-${activeVariant.key}`}>
                    HTML-Code
                  </label>
                  <SlashCommandToolbar
                    onSelect={(snippet) =>
                      updateVariant(activeVariantIndex, { html: activeVariant.html + snippet })
                    }
                  />
                  <textarea
                    id={`html-${activeVariant.key}`}
                    className="input"
                    rows={12}
                    value={activeVariant.html}
                    onChange={(e) => updateVariant(activeVariantIndex, { html: e.target.value })}
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}
                  />
                </div>

                {/* ABMELDE-FUSSZEILE OPTIONAL SCHALTEN */}
                <div
                  style={{
                    padding: '0.85rem 1rem',
                    background: 'rgba(30, 41, 59, 0.5)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#f8fafc' }}>
                      🔗 Abmelde-Link &amp; Fußzeile automatisch am Ende der E-Mail einfügen
                    </div>
                    <div className="text-xs text-muted" style={{ marginTop: '0.2rem' }}>
                      {type === 'direct'
                        ? 'Deaktiviert (Empfohlen für 1-zu-1 Firmen-E-Mails an Kunden)'
                        : 'Aktiviert (Rechtliche Fußzeile für Marketing & Newsletter)'}
                    </div>
                  </div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={includeUnsubscribeFooter}
                      onChange={(e) => setIncludeUnsubscribeFooter(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span className="text-sm" style={{ fontWeight: 600, color: includeUnsubscribeFooter ? '#34d399' : '#94a3b8' }}>
                      {includeUnsubscribeFooter ? 'Ein' : 'Aus'}
                    </span>
                  </label>
                </div>

                {/* SPAM CHECK BADGE */}
                <div
                  className="card"
                  style={{
                    padding: 'var(--space-3)',
                    background: 'var(--color-surface-hover)',
                    borderColor:
                      spam.rating === 'kritisch'
                        ? 'var(--color-danger)'
                        : spam.rating === 'mittel'
                          ? 'var(--color-warning)'
                          : 'var(--color-border)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>🛡️ Pre-Flight Spam-Check: {spam.rating.toUpperCase()}</strong>
                    <span className="badge" data-tone={spam.score > 30 ? 'warning' : 'success'}>
                      Spam-Score: {spam.score} / 100
                    </span>
                  </div>
                  {spam.issues.length > 0 && (
                    <ul className="text-xs muted" style={{ marginTop: 'var(--space-2)', paddingLeft: 'var(--space-4)' }}>
                      {spam.issues.map((issue, idx) => (
                        <li key={idx}>{issue.message}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* LIVE PREVIEW COLUMN */}
        {(viewMode === 'split' || viewMode === 'preview') && (
          <section className="preview-frame-wrap">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong className="text-sm">Echtzeit Live-Vorschau</strong>
                <div className="text-xs muted">Gefüllt mit Beispiel-Daten (Max Mustermann)</div>
              </div>
              <div className="cluster" style={{ gap: 'var(--space-2)' }}>
                <Button
                  variant="ghost"
                  onClick={() => setSpintaxSeed((s) => s + 1)}
                  title="Andere Spintax-Zufallsvariante testen"
                >
                  🎲 Spintax testen
                </Button>
                <div className="button-group">
                  <Button
                    variant={device === 'desktop' ? 'primary' : 'ghost'}
                    onClick={() => setDevice('desktop')}
                  >
                    🖥️ Desktop
                  </Button>
                  <Button
                    variant={device === 'mobile' ? 'primary' : 'ghost'}
                    onClick={() => setDevice('mobile')}
                  >
                    📱 Mobil
                  </Button>
                </div>
              </div>
            </div>

            {/* SIMULATED EMAIL CLIENT FRAME */}
            <div className={device === 'desktop' ? 'preview-device-desktop' : 'preview-device-mobile'}>
              <div
                style={{
                  borderBottom: '1px solid #e5e7eb',
                  padding: '12px 16px',
                  background: '#f9fafb',
                  fontSize: '13px',
                  color: '#374151',
                }}
              >
                <div>
                  <strong>Von:</strong> {fromName || 'Ihr Name'} &lt;{fromEmail || 'absender@beispiel.ch'}&gt;
                </div>
                <div>
                  <strong>An:</strong> Max Mustermann &lt;max.mustermann@example.com&gt;
                </div>
                <div style={{ marginTop: '4px', fontWeight: 600, color: '#111827' }}>
                  {preview.previewSubject}
                </div>
              </div>
              <iframe
                ref={previewIframeRef}
                title="E-Mail Live Vorschau"
                onLoad={writePreviewDoc}
                style={{ width: '100%', height: 'calc(100% - 70px)', border: 'none' }}
              />
            </div>
          </section>
        )}
      </div>

      <div className="dialog__actions">
        <Link to="/kampagnen" className="btn" data-variant="secondary">
          Abbrechen
        </Link>
        <Button
          variant="secondary"
          onClick={async () => {
            try {
              await sichereEntwurf();
              await navigate({ to: '/kampagnen' });
            } catch (err) {
              alert(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
            }
          }}
          loading={create.isPending || update.isPending}
        >
          Als Entwurf speichern
        </Button>
        <Button onClick={() => setStartdialog(true)} disabled={!ready}>
          {scheduleMode === 'scheduled' ? 'Terminieren …' : 'Senden …'}
        </Button>
      </div>

      {startdialog && (
        <StartDialog
          campaignId={campaignId}
          kampagnenname={name}
          betreff={activeVariant.subject}
          html={activeVariant.html}
          empfaenger={netto}
          profil={selectedPolicy}
          laeuft={create.isPending || update.isPending}
          onAbbrechen={() => setStartdialog(false)}
          onSenden={() => void save()}
        />
      )}
    </div>
  );
}
